import clsx from 'clsx';
import { type ComponentPropsWithoutRef, forwardRef } from 'react';

const RADIUS = 160;
const STROKE_WIDTH = 140;

export type CircularProgressSegmentKind =
    | 'primary'
    | 'secondary'
    | 'success'
    | 'done'
    | 'danger'
    | 'warning'
    | 'upgrade'
    | 'merge';

export interface CircularProgressSegment {
    /**
     * Value of the segment between 0 and 100.
     */
    value: number;
    /**
     * Visual style of the segment.
     * @default 'done'
     */
    kind?: CircularProgressSegmentKind;
}

export interface CircularProgressProps extends ComponentPropsWithoutRef<'div'> {
    /**
     * Segments to render inside the progress indicator.
     */
    segments: CircularProgressSegment[];
}

const SEGMENT_KIND_CLASSNAMES: Record<CircularProgressSegmentKind, string> = {
    primary: 'stroke-[--sp-color-action-bg-primary-default]',
    secondary: 'stroke-[--sp-color-action-bg-secondary-default]',
    success: 'stroke-[--sp-color-action-bg-success-default]',
    done: 'stroke-[--sp-color-action-bg-success-default]',
    danger: 'stroke-[--sp-color-action-bg-danger-default]',
    warning: 'stroke-[--sp-color-action-bg-warning-default]',
    upgrade: 'stroke-[--sp-color-action-bg-upgrade-default]',
    merge: 'stroke-[--sp-color-action-bg-merge-default]',
};

/**
 * Circular progress indicator with multiple segments.
 */
export const CircularProgress = forwardRef<HTMLDivElement, CircularProgressProps>(
    ({ segments, className, ...props }, ref) => {
        const normalizedSegments = segments.map((segment) => ({
            value: Math.max(0, Math.min(100, segment.value)),
            kind: segment.kind ?? 'done',
        }));

        const ariaValueNow = normalizedSegments.reduce((total, segment) => {
            if (total >= 100) {
                return 100;
            }
            const remaining = 100 - total;
            return total + Math.min(segment.value, remaining);
        }, 0);

        let consumed = 0;

        const isAnimated = normalizedSegments.length === 1;

        return (
            <div
                ref={ref}
                role="progressbar"
                aria-valuenow={ariaValueNow}
                aria-valuemin={0}
                aria-valuemax={100}
                className={clsx(className, 'relative overflow-hidden rounded-full')}
                {...props}
            >
                {/* biome-ignore lint/a11y/noSvgWithoutTitle: no title needed here */}
                <svg viewBox="0 0 320 320" className="-rotate-90 absolute inset-px rounded-full">
                    <circle
                        fill="none"
                        strokeWidth={STROKE_WIDTH}
                        r={RADIUS}
                        cx={RADIUS}
                        cy={RADIUS}
                        className="stroke-[--sp-color-border-base]"
                    />

                    {normalizedSegments.map((segment, index) => {
                        const available = Math.max(0, 100 - consumed);
                        const value = Math.min(segment.value, available);

                        const consumedRatio = consumed / 100;
                        const ratio = value / 100;

                        consumed += value;

                        return (
                            <circle
                                key={`${segment.kind}-${index}`}
                                fill="none"
                                strokeWidth={STROKE_WIDTH}
                                r={RADIUS}
                                cx={RADIUS}
                                cy={RADIUS}
                                className={clsx(
                                    SEGMENT_KIND_CLASSNAMES[segment.kind],
                                    'transition-[stroke-dashoffset]'
                                )}
                                style={{
                                    ...(isAnimated
                                        ? {
                                              strokeDasharray: Math.PI * RADIUS * 2,
                                              strokeDashoffset:
                                                  (1 - consumedRatio - ratio) *
                                                  (Math.PI * RADIUS * 2),
                                          }
                                        : {
                                              strokeDasharray: `0 ${consumedRatio * Math.PI * RADIUS * 2} ${ratio * Math.PI * RADIUS * 2} ${(1 - ratio) * Math.PI * RADIUS * 2}`,
                                          }),
                                    transitionDuration: '0.2s',
                                    transitionTimingFunction: 'linear',
                                }}
                            />
                        );
                    })}
                </svg>
            </div>
        );
    }
);
